import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSiteConfig } from "@/contexts/SiteConfigContext";
import api from "@/lib/api";
import { Arrow, Barcode, Wheel, initials } from "@/components/site/parts";
import { useCopy } from "@/lib/copy";

const ROW_COLORS = ["#FFC83D", "#D5D8DF", "#E0995E", "#A3A8B4"];
const HOBBY_COLORS = ["#FFC83D", "#E8EEFF", "#DFF7E8", "#FFE1DE"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmt(v) {
  if (!v) return "";
  const [y, m] = v.split("-");
  return `${MONTHS[Number(m) - 1] || ""} ${y}`.trim();
}

export default function About() {
  const { config } = useSiteConfig();
  const t = useCopy();
  const [events, setEvents] = useState([]);
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    api.get("/timeline").then(({ data }) => setEvents(data)).catch(() => {});
    api.get("/skills").then(({ data }) => setSkills(data)).catch(() => {});
  }, []);

  const bio = (config.bio || "").split(/\n{2,}/).filter(Boolean);
  const notes = config.setup_notes || [];
  const hobbies = config.workbench || [];

  return (
    <>
      <section className="bx" style={{ paddingTop: 16 }}>
        <div className="card rise d1 padl split" style={{ padding: 48 }}>
          <div className="profile-card bob">
            <div className="profile-card-shine" aria-hidden="true" />
            <span className="mono" style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase" }}>{t("about.card_label")}</span>
            <WidePhoto name={config.name} src={config.profile_image_url} />
            <dl className="mono" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, fontSize: 13 }}>
              <div><dt>NAME</dt><dd>{config.name}</dd></div>
              <div><dt>ROLE</dt><dd>{config.role}</dd></div>
              {config.location && <div><dt>LOCATION</dt><dd>{config.location}</dd></div>}
              <div><dt>YEARS OF EXPERIENCE</dt><dd>{config.years_experience}</dd></div>
            </dl>
            <Barcode />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            <span className="kicker">{t("about.kicker")}</span>
            <h1 className="hero-h stretch" style={{ fontSize: 64, color: "#15171C", animationDelay: ".2s" }}>
              {t("home.about_title")} <span className="em">{t("home.about_accent")}</span> {t("home.about_title_end")}
            </h1>
            {bio.map((p, i) => <p key={i} className="lede" style={{ fontSize: 18, color: "#2B2E35" }}>{p}</p>)}
            {config.tagline && <p className="lede" style={{ fontSize: 18, color: "#2B2E35" }}>{config.tagline}</p>}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {config.resume_url && (
                <a href={config.resume_url} target="_blank" rel="noopener noreferrer" className="btn btn-ink" style={{ height: 52, borderRadius: 14, boxShadow: "0 5px 0 #0B0C0F" }}>
                  {t("about.resume")}
                </a>
              )}
              <Link to="/contact" className="btn btn-line" style={{ height: 52, borderRadius: 14 }}>{t("nav.contact")} <Arrow /></Link>
            </div>
          </div>
        </div>
      </section>

      {events.length > 0 && (
        <section className="bx sec">
          <div className="sec-head rv">
            <div>
              <span className="kicker">{t("about.experience_kicker")}</span>
              <h2 className="h-sec" style={{ fontSize: 48 }}>{t("about.experience_title")}</h2>
            </div>
            <p style={{ margin: 0, maxWidth: 380, fontSize: 15, lineHeight: 1.6, color: "#4A4F5A" }}>
              {t("about.experience_sub")}
            </p>
          </div>
          <div className="card-ink rv" style={{ borderRadius: 24, overflow: "hidden" }}>
            <div className="experience-row mono" style={{ padding: "14px 28px", fontSize: 11, color: "#A3A8B4", borderBottom: "1px solid #2C3039" }}>
              <span>NO.</span><span>ROLE AND COMPANY</span><span className="experience-hide">HIGHLIGHT</span><span className="experience-hide">DATES</span>
            </div>
            {events.map((e, i) => (
              <div key={e.id} className="experience-row" style={{ padding: "22px 28px", borderBottom: i < events.length - 1 ? "1px solid #2C3039" : 0 }} data-testid={`timeline-item-${e.id}`}>
                <span className="disp" style={{ fontSize: 28, fontWeight: 900, fontStyle: "italic", color: ROW_COLORS[Math.min(i, 3)] }}>{String(events.length - i).padStart(2, "0")}</span>
                <span style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                  <span style={{ fontSize: 18, fontWeight: 600 }}>{e.role}</span>
                  <span style={{ fontSize: 14, color: "#A3A8B4" }}>{[e.company, e.location].filter(Boolean).join(" · ")}</span>
                </span>
                <span className="experience-hide" style={{ fontSize: 15, color: "#D5D8DF" }}>{e.highlights?.[0] || e.description}</span>
                <span className="experience-hide mono" style={{ fontSize: 13, color: "#A3A8B4" }}>{fmt(e.start_date)} to {e.end_date ? fmt(e.end_date) : "now"}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {notes.length > 0 && (
        <section className="bx sec">
          <div className="sec-head rv">
            <div>
              <span className="kicker">{t("about.principles_kicker")}</span>
              <h2 className="h-sec" style={{ fontSize: 48 }}>{t("about.principles_title")}</h2>
            </div>
          </div>
          <div className="g3">
            {notes.map((n, i) => (
              <div key={i} className="rv project-card" style={{ padding: 24, gap: 12 }}>
                {n.knob && <span className="mono" style={{ alignSelf: "flex-start", fontSize: 11, padding: "4px 8px", background: "#FFC83D", borderRadius: 6, fontWeight: 600, textTransform: "uppercase" }}>{n.knob}</span>}
                <span className="disp" style={{ fontSize: 22, fontWeight: 800, textTransform: "uppercase" }}>{n.title}</span>
                <span style={{ fontSize: 15, lineHeight: 1.6, color: "#4A4F5A" }}>{n.desc}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {skills.length > 0 && (
        <section id="skills" className="bx sec">
          <div className="card-ink rv padl" style={{ padding: 44 }}>
            <div className="sec-head" style={{ marginBottom: 28 }}>
              <div>
                <span className="kicker on-dark">{t("about.skills_kicker")}</span>
                <h2 className="h-sec" style={{ fontSize: 48 }}>{t("about.skills_title")}</h2>
              </div>
              <span className="hide-m"><Wheel /></span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {skills.map((s) => (
                <span key={s.id} className="skill-tag" title={s.category}>
                  {s.name}
                  {s.years ? <span className="mono" style={{ fontSize: 11, color: "#6B6A63" }}>{s.years}y</span> : null}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {hobbies.length > 0 && (
        <section className="bx sec">
          <div className="sec-head rv">
            <div>
              <span className="kicker">{t("about.hobbies_kicker")}</span>
              <h2 className="h-sec" style={{ fontSize: 48 }}>{t("about.hobbies_title")}</h2>
            </div>
            <p style={{ margin: 0, maxWidth: 380, fontSize: 15, lineHeight: 1.6, color: "#4A4F5A" }}>{t("about.hobbies_sub")}</p>
          </div>
          <div className="g4">
            {hobbies.map((r, i) => (
              <figure key={i} className="rv project-card" style={{ margin: 0, border: "1px solid #DDD7C9" }}>
                <div style={{ height: 160, background: HOBBY_COLORS[i % 4], display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  {r.image_url ? (
                    <img src={r.image_url} alt={r.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <svg aria-hidden="true" width="120" height="60" viewBox="0 0 120 60" fill="none">
                      <rect x="14" y="20" width="92" height="22" rx="8" fill="#15171C" />
                      <rect x="40" y="10" width="34" height="14" rx="5" fill="#15171C" />
                      <circle className="wheel" cx="32" cy="44" r="10" fill="#2C3039" stroke="#FFC83D" strokeWidth="2" strokeDasharray="4 4" />
                      <circle className="wheel" cx="88" cy="44" r="10" fill="#2C3039" stroke="#FFC83D" strokeWidth="2" strokeDasharray="4 4" />
                    </svg>
                  )}
                </div>
                <figcaption style={{ padding: 16, display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontWeight: 600 }}>{r.name}</span>
                  {r.spec && <span className="mono" style={{ fontSize: 12, color: "#6B6A63" }}>{r.spec}</span>}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function WidePhoto({ name, src }) {
  const [failed, setFailed] = useState(false);
  return (
    <div style={{ aspectRatio: "4 / 3", borderRadius: 12, background: "#C9D5FF", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      {!failed ? (
        <img src={src || "/profile.jpeg"} alt={`Portrait of ${name}`} onError={() => setFailed(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <span className="disp" style={{ fontSize: 64, fontWeight: 900, color: "#3A4A8F" }}>{initials(name)}</span>
      )}
    </div>
  );
}
